import { forwardRef, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Drawer, type DialogProps } from 'vaul'
import { X } from 'lucide-react'
import { IconButton } from './IconButton'

/** D1's two stops: three fifths of the screen, then all of it. Callers do not
 *  get to pick their own, or every sheet in the app opens at a different
 *  height. */
const SNAP_POINTS = [0.6, 1] as const
const TOP_SNAP = SNAP_POINTS[SNAP_POINTS.length - 1]

/** Vaul's own transition, `TRANSITIONS.DURATION` in its source (0.5s), plus a
 *  frame: the field's box is only where it will stay once the snap has
 *  finished moving the sheet. */
const SNAP_SETTLE_MS = 550

/** The controls that bring up a keyboard, or on iOS the picker that takes
 *  its place at the foot of the screen. A checkbox, a radio or a button
 *  does neither, and snapping the sheet for one would move it under a
 *  finger that only meant to tick something. */
function isField(el: EventTarget | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true
  if (!(el instanceof HTMLInputElement)) return false
  return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image', 'hidden']
    .includes(el.type)
}

/** The nearest ancestor of `el`, inside `root`, that scrolls vertically —
 *  in practice `.u-sheet__body`, but read from the computed style rather
 *  than the class so a caller that scrolls a region of its own is found. */
function scrollerOf(el: HTMLElement, root: HTMLElement): HTMLElement | null {
  for (let n = el.parentElement; n && n !== root.parentElement; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY
    if (oy === 'auto' || oy === 'scroll') return n
  }
  return null
}

/**
 * Put `field` in the middle of the part of its scroller that is actually on
 * screen. "On screen" is the scroller's box clipped to the *visual*
 * viewport — the one the iOS keyboard shrinks and may pan — not to the
 * layout viewport a `position: fixed` sheet is placed in, which the keyboard
 * leaves alone. Centring on the scroller's own box instead would centre the
 * field behind the keyboard whenever the sheet reaches under it.
 */
function centreInView(field: HTMLElement, scroller: HTMLElement) {
  const vv = window.visualViewport
  const s = scroller.getBoundingClientRect()
  const f = field.getBoundingClientRect()
  const top = Math.max(s.top, vv ? vv.offsetTop : 0)
  const bottom = Math.min(s.bottom, vv ? vv.offsetTop + vv.height : window.innerHeight)
  if (bottom - top < f.height) {
    // Nothing of the scroller is left on screen to centre in (a sheet pushed
    // wholly above or below the visible band). Let the browser choose; it
    // knows the visual viewport better than a rectangle does.
    field.scrollIntoView({ block: 'center' })
    return
  }
  scroller.scrollTop += (f.top + f.height / 2) - (top + bottom) / 2
}

/**
 * A focused field inside a sheet snaps the sheet to its top point and
 * scrolls the field into the middle of what is visible (WP 10.5, Doug's
 * visitor form: typing into *Who* left a blank page over the keyboard with
 * the field he was typing into gone).
 *
 * Installed by every `Sheet` on its own content, so the Year's forms and
 * `EventSheet` share one behaviour rather than each growing a version;
 * exported for a caller that draws a drawer of its own. `snapToTop` is the
 * caller's way to raise the sheet — `Sheet` passes its own snap setter, and
 * a sheet with no snap points (already at its only height) passes nothing.
 *
 * The scroll is tried more than once, on purpose: at the next frame (a
 * field already on screen, a keyboard already up), on every visual-viewport
 * resize while the field keeps focus (the keyboard arriving, or its
 * suggestion bar growing), and once the snap has finished moving the sheet.
 * Each try measures afresh, so a later one corrects an earlier one and none
 * of them fights another. It all stops when the field loses focus.
 *
 * What this cannot do is show itself working: no harness can open an iOS
 * keyboard, so the visual-viewport half is written against the platform's
 * documented behaviour and is checked on Doug's phone.
 *
 * Returns the cleanup.
 */
export function focusSnapsToTop(content: HTMLElement, snapToTop?: () => void): () => void {
  let stopCurrent: (() => void) | null = null

  function onFocusIn(ev: FocusEvent) {
    const field = ev.target
    if (!isField(field)) return
    stopCurrent?.()
    snapToTop?.()

    const scroller = scrollerOf(field, content)
    const place = () => { if (scroller && document.activeElement === field) centreInView(field, scroller) }
    const frame = requestAnimationFrame(place)
    const settle = setTimeout(place, SNAP_SETTLE_MS)
    const vv = window.visualViewport
    vv?.addEventListener('resize', place)

    const stop = () => {
      cancelAnimationFrame(frame)
      clearTimeout(settle)
      vv?.removeEventListener('resize', place)
      field.removeEventListener('blur', stop)
      if (stopCurrent === stop) stopCurrent = null
    }
    field.addEventListener('blur', stop)
    stopCurrent = stop
  }

  content.addEventListener('focusin', onFocusIn)
  return () => {
    content.removeEventListener('focusin', onFocusIn)
    stopCurrent?.()
  }
}

export interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Set in `--t-title` serif. Optional, but a sheet without one is anonymous
   *  to a screen reader — when it is absent a hidden title stands in. */
  title?: string
  /** Open at full height, with a stop at three fifths on the way down.
   *  Without it the sheet is as tall as its content, up to 88vh. */
  snap?: boolean
  /** A bar pinned to the foot, outside the scrolling area. */
  actions?: ReactNode
  children?: ReactNode
  className?: string
  /**
   * A sub-sheet (RateSheet, "wrong", Add to list) is open on top of this one.
   * `EventActions` keeps its own menu Sheet's `open` hardcoded `true` rather
   * than tying it to which view is showing, because an early return that
   * unmounted it that way once left Vaul's scrim behind in the DOM,
   * intercepting every pointer event for whatever replaced it (see
   * `EventActions.tsx`'s own note). So it stays mounted and open, and this
   * hides its chrome instead: opacity, pointer events and `aria-hidden`, on
   * both the content and its own scrim, so a second sheet opening on top
   * does not draw two grab handles and one title twice — the WP 8.9 fix.
   */
  chromeHidden?: boolean
  /**
   * A × at the end of the title row (UI-audit a2-40, Doug chose "only on deep
   * stacks"). Scrim, drag, Escape and Back all close every sheet, but on a
   * sheet stacked on another — Account, Venue admin, the venue editor, a
   * sheet opened from the event sheet — nothing on screen says so, and a
   * tall one (Account with "All 205" open is ~14,700px) has no foot to reach.
   * Off by default: on a short sheet the scrim is the close button. It calls
   * `onOpenChange(false)`, the same path a scrim tap takes, so the history
   * entry is popped exactly as it is for every other way out.
   */
  closeButton?: boolean
  /** Radix's own hook, passed through: called as the sheet closes, before it
   *  hands focus back to whatever opened it. A row's "…" menu
   *  (`components/RowMore.tsx`) prevents that when an action was picked —
   *  Edit has just mounted an input with `autoFocus`, and focus going back
   *  to the "…" would blur it, which saves and leaves edit mode at once. */
  onCloseAutoFocus?: (ev: Event) => void
}

/**
 * Every mounted-and-open sheet, in the order it was opened. Module state,
 * because sheets stack (Account → Venue admin → Venue editor) and each is its
 * own component instance with no other way to know about the others.
 *
 * Vaul brings the focus trap, the scrim, Escape, drag-to-dismiss and the snap
 * points. What it does not bring is a place in the browser's history, and
 * without one the phone's back gesture leaves the sheet up and navigates the
 * tab underneath it (UI-audit F08) — so that half is ported from
 * `components/Sheet.tsx`, along with the reason it is one module-level
 * listener rather than one per instance: closing the top sheet by any means
 * has to pop the entry it pushed, and that pop's own `popstate` reaches the
 * sheet below exactly the way a real back-press would.
 */
let stack: { id: number; close: () => void; poppedByUser: boolean }[] = []
let nextId = 0
let listenersInstalled = false

/** Set for the one `popstate` a cleanup's own `history.back()` is about to
 *  cause, so the listener knows that pop is bookkeeping for the sheet that
 *  already closed and not a new back-press for whichever is now on top. */
let suppressNextPopstate = false

function installGlobalListeners() {
  if (listenersInstalled) return
  listenersInstalled = true
  window.addEventListener('popstate', () => {
    if (suppressNextPopstate) { suppressNextPopstate = false; return }
    const top = stack[stack.length - 1]
    if (!top) return
    top.poppedByUser = true
    top.close()
  })
}

export const Sheet = forwardRef<HTMLDivElement, SheetProps>(function Sheet(
  { open, onOpenChange, title, snap = false, actions, children, className = '', chromeHidden = false, closeButton = false, onCloseAutoFocus },
  ref,
) {
  // WP 10.2: opens at the top point. Three fifths put the picture and the
  // title on screen and everything a decision is made on a drag away (Doug:
  // "you have to drag it again to see anything"). The lower point stays, on
  // the way down to closed.
  const [active, setActive] = useState<number | string | null>(SNAP_POINTS[1])

  // The content node, for `focusSnapsToTop`. A callback ref, because the
  // node arrives when Vaul's portal mounts it, which is after this
  // component's own first effect, and leaves again when the sheet closes —
  // an effect keyed on `open` would read it before it exists. The forwarded
  // ref is kept in step by hand, since a caller may pass one of its own.
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    setContent(node)
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])

  useEffect(() => {
    if (!content) return
    return focusSnapsToTop(content, snap ? () => setActive(TOP_SNAP) : undefined)
  }, [content, snap])

  // Callers pass an inline `onOpenChange`, a fresh function on every render of
  // whatever owns `open`. Read through a ref so pushing and popping history is
  // not redone every time an unrelated parent re-render hands this the same
  // meaning in a new function.
  const changeRef = useRef(onOpenChange)
  changeRef.current = onOpenChange

  // Assigned during render, not inside the effect, so it survives React 18
  // StrictMode's deliberate unmount-and-remount of every effect: the remount
  // pushes the *same* id back onto the stack, which is what the cleanup below
  // checks for before popping history.
  const id = useRef(-1)
  if (id.current < 0) id.current = nextId++

  useEffect(() => {
    if (!open) return
    installGlobalListeners()
    const me = id.current
    const entry = { id: me, close: () => changeRef.current(false), poppedByUser: false }
    stack.push(entry)
    history.pushState({ uSheet: me }, '')

    return () => {
      stack = stack.filter(en => en !== entry)
      if (entry.poppedByUser) return
      // Deferred a microtask: if this was StrictMode's double-invoke, the
      // remount's push has already happened by the time this runs, and a real
      // close — nothing left with this id — still pops cleanly. The
      // `history.state` check guards the other case, a tab switch that pushed
      // its own entry before this cleanup ran; calling `back()` past that
      // would undo the tab switch instead of closing this sheet.
      queueMicrotask(() => {
        if (stack.some(en => en.id === me)) return
        if ((history.state as { uSheet?: number } | null)?.uSheet === me) {
          suppressNextPopstate = true
          history.back()
        }
      })
    }
  }, [open])

  // Back to the top for the next opening. Without this a sheet dragged down
  // to three fifths opens there for every caller after it, which is the sheet
  // remembering a gesture that belonged to one use of it.
  useEffect(() => { if (!open) setActive(SNAP_POINTS[1]) }, [open])

  const full = !snap || active === 1
  const classes = [
    'u-sheet', snap ? 'u-sheet--snap' : '', full ? 'is-full' : '', className,
  ].filter(Boolean).join(' ')

  /**
   * Handed over as one object, not as four conditional props. Vaul's props are
   * a union — snap points come *with* a `fadeFromIndex` or not at all — and a
   * `fadeFromIndex={snap ? 0 : undefined}` cannot be either branch, so the two
   * halves travel together or not at all.
   *
   * `fadeFromIndex: 0` is the correction the first render of this asked for:
   * Vaul fades the scrim in from the *last* snap point by default, so a sheet
   * resting at three fifths had no scrim at all and read as a panel that had
   * failed to open. This sheet is modal at every height — focus trapped,
   * Escape closes — so the scrim is there from the first snap.
   */
  const snapping: DialogProps = snap
    ? { snapPoints: [...SNAP_POINTS], fadeFromIndex: 0, activeSnapPoint: active, setActiveSnapPoint: setActive }
    : {}

  // The chrome-hidden style, inline rather than a class: this sheet stays
  // `open` (Vaul keeps managing it, so nothing unmounts and no scrim is left
  // behind — see `chromeHidden`'s own note above) but must draw as if it
  // were not there at all, the same three properties `checks.mjs`'s own
  // `visible()` already reads as "not on screen".
  const hiddenStyle = chromeHidden ? { opacity: 0, pointerEvents: 'none' as const } : undefined

  // `repositionInputs={false}`: Vaul's own keyboard handling lifts the
  // drawer by the keyboard's height and shrinks it to what it measures,
  // without accounting for the visual viewport Safari has already panned
  // to reveal the field — Doug's 29 Sept screenshot is the sheet's lower
  // half at the top of the screen with a blank band under it (10.5 named
  // this as the next lever). Off, iOS scrolls the field into view inside
  // the sheet's own scrolling body, which every non-snapping sheet has.
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false} {...snapping}>
      <Drawer.Portal>
        <Drawer.Overlay className="u-scrim" style={hiddenStyle} aria-hidden="true" />
        <Drawer.Content
          ref={contentRef} className={classes} style={hiddenStyle} aria-hidden={chromeHidden || undefined}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <div className="u-sheet__drag">
            <Drawer.Handle className="u-sheet__grab" />
          </div>
          {closeButton ? (
            <div className="u-sheet__head">
              <Drawer.Title className={title ? 'u-sheet__title' : 'u-sr'}>
                {title ?? 'Sheet'}
              </Drawer.Title>
              <IconButton label="Close" title="Close" className="u-sheet__close" onClick={() => onOpenChange(false)}>
                <X size={20} />
              </IconButton>
            </div>
          ) : (
            <Drawer.Title className={title ? 'u-sheet__title' : 'u-sr'}>
              {title ?? 'Sheet'}
            </Drawer.Title>
          )}
          <div className="u-sheet__body">{children}</div>
          {actions && <div className="u-sheet__actions">{actions}</div>}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})
